import { useState, useEffect, useCallback, useRef } from "react";
import { useAccount } from "./auth";
import { KEYS } from "./backup";
import { readStamps, writeStamps, STAMPS_KEY } from "./stamps";
import { reconcile, takeAll, forgetAccountCopy, ACCOUNT_KEY } from "./reconcile";

/**
 * Keeping a signed-in person's collection in step across devices.
 *
 * Signed out this does nothing at all, which is the point: the app has always
 * worked with everything in one browser, and it still does. Signing in adds a
 * copy somewhere else; it does not become the way the app works.
 *
 * The reconciling itself lives in reconcile.js as a pure function. What is
 * here is the plumbing around it — when to look, what to write, and how not to
 * write a thing that was only just read.
 */

const SYNC_MS = 4000;   // how long a change sits before it goes up

/** Everything the app keeps, in the shape the endpoint speaks. */
function snapshot(store = window.localStorage) {
  const entries = {};
  for (const { key } of KEYS) {
    const raw = store.getItem(key);
    if (raw != null) entries[key] = raw;
  }
  return { entries, stamps: readStamps(store) };
}

function writeLocal(apply, applyStamps, store = window.localStorage) {
  const keys = Object.keys(apply);
  if (!keys.length) return false;
  for (const key of keys) store.setItem(key, apply[key]);
  writeStamps({ ...readStamps(store), ...applyStamps }, store);
  return true;
}

export default function useSync() {
  const { enabled, ready, signedIn, userId } = useAccount();

  /* Signing out puts the account's copy away. Guarded on `ready` because Clerk
     reports signed-out while it is still deciding, and on `enabled` because an
     app with accounts switched off must never clear anything — there would be
     no way back in to retrieve it. */
  useEffect(() => {
    if (!enabled || !ready || signedIn) return;
    const keys = KEYS.map((k) => k.key);
    if (forgetAccountCopy(keys, STAMPS_KEY, window.localStorage)) {
      /* Every screen reads its storage once, on mount. Without this the cards
         would stay on screen while the storage behind them was empty. */
      window.location.reload();
    }
  }, [enabled, ready, signedIn]);
  const [state, setState] = useState("idle");   // idle | syncing | synced | error
  const [conflict, setConflict] = useState(null);
  /* Put away, not answered. The dialog told people to close it and take a
     backup first and gave them no way to do either, so it needed a way out —
     but a way out must not be mistaken for a decision. While a conflict
     stands, syncing stays stopped whether or not the dialog is on screen,
     because the alternative is that closing a box quietly uploads one of two
     collections over the other. */
  const [hidden, setHidden] = useState(false);
  const running = useRef(false);

  /* One round trip does both halves: send what is newer here, and be told
     everything the account holds afterwards. */
  const post = useCallback(async (payload) => {
    const res = await fetch("/api/sync", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`sync ${res.status}`);
    return res.json();
  }, []);

  /** The first look after signing in, which is the only one that can ask. */
  const reconcileNow = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    setState("syncing");
    try {
      const res = await fetch("/api/sync", { cache: "no-store" });
      if (!res.ok) throw new Error(`sync ${res.status}`);
      const server = await res.json();
      const local = snapshot();
      const known = window.localStorage.getItem(ACCOUNT_KEY) === userId;

      const plan = reconcile(local, server, known);

      if (plan.ask) {
        /* Stop. Nothing is written and nothing is sent until a person picks,
           because this is the one case where a wrong guess destroys work that
           only exists in one place. */
        setConflict({ local, server, conflicts: plan.conflicts });
        setHidden(false);
        setState("idle");
        return;
      }

      writeLocal(plan.apply, plan.applyStamps);
      if (Object.keys(plan.push.entries).length) await post(plan.push);
      window.localStorage.setItem(ACCOUNT_KEY, userId);
      setState("synced");
      if (Object.keys(plan.apply).length) window.location.reload();
    } catch {
      setState("error");
    } finally {
      running.current = false;
    }
  }, [userId, post]);

  /** Answering the question: keep this device, or keep the account. */
  const resolve = useCallback(
    async (choice) => {
      if (!conflict) return;
      setState("syncing");
      try {
        if (choice === "device") {
          await post(takeAll(conflict.local));
        } else {
          const { entries, stamps } = takeAll(conflict.server);
          writeLocal(entries, stamps);
        }
        window.localStorage.setItem(ACCOUNT_KEY, userId);
        setConflict(null);
        setState("synced");
        window.location.reload();
      } catch {
        setState("error");
      }
    },
    [conflict, post, userId]
  );

  // First look, once signing in has settled.
  useEffect(() => {
    if (!signedIn || !userId) { setState("idle"); return; }
    reconcileNow();
  }, [signedIn, userId, reconcileNow]);

  /* After that, push on a timer rather than on every keystroke. Storage has no
     change event for the tab that made the change, and a poll that only sends
     when a date has moved costs nothing when nothing has. */
  useEffect(() => {
    if (!signedIn || !userId || conflict) return undefined;
    let last = JSON.stringify(readStamps());
    const t = setInterval(async () => {
      if (running.current) return;
      const now = JSON.stringify(readStamps());
      if (now === last) return;
      last = now;
      running.current = true;
      try {
        await post(snapshot());
        setState("synced");
      } catch {
        setState("error");
      } finally {
        running.current = false;
      }
    }, SYNC_MS);
    return () => clearInterval(t);
  }, [signedIn, userId, conflict, post]);

  return {
    state,
    /* Hidden hides the dialog and nothing else. `paused` is the honest state
       of the app afterwards, and something has to say so out loud — a sync
       that has stopped and does not mention it is how a day goes by with two
       devices quietly disagreeing. */
    conflict: hidden ? null : conflict,
    paused: Boolean(conflict),
    dismiss: useCallback(() => setHidden(true), []),
    reopen: useCallback(() => setHidden(false), []),
    resolve,
    syncNow: reconcileNow,
  };
}
